import { useState } from 'react'
import { Provider, useDispatch } from 'react-redux'
import useAsyncEffect from 'use-async-effect'
import { IntercomProvider } from 'react-use-intercom'
import { BrowserRouter as Router } from 'react-router-dom'
import { SplitFactoryProvider } from '@splitsoftware/splitio-react'
import { Grid } from '@mui/material_modern'
import { Theme, ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'

import store from 'state/store'
import { setCurrentAuthToken } from 'state/auth'

import AppRoutes from 'AppRoutes'
import AppSetup from 'AppSetup'
import CriticalDataErrorModal from 'components/CriticalDataErrorModal'
import InactivityHandler from 'components/InactivityHandler'
import ErrorBoundary from 'components/generic/errorboundary/ErrorBoundary'
import Loading from 'components/generic/Loading'

import theme from 'themes/landing'
import { isAmplifyDevEnv, isProduction } from 'utils/envs'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import './global.css'
import 'auth/cognito.css'

declare module '@mui/styles/defaultTheme' {
  interface DefaultTheme extends Theme {}
}

const INTERCOM_APP_ID = 'x6adqppn'

const GlobalLoader = () => {
  const { criticalDataLoading, loading } = useAppSelector(state => state.loading)
  return criticalDataLoading || loading ? <Loading /> : null
}

const AppOrLoader = () => {
  const dispatch = useDispatch()
  const [isAuthInit, setIsAuthInit] = useState(false)

  useAsyncEffect(async () => {
    dispatch(setCurrentAuthToken())
    setIsAuthInit(true)
  }, [])

  return (
    <div className="app" data-testid="test-app-container">
      <GlobalLoader />
      <AppSetup />
      <InactivityHandler />
      <CriticalDataErrorModal />
      {isAuthInit ? <AppRoutes /> : <Loading />}
    </div>
  )
}

const App = () => {
  // don't load app if REACT_APP_SPLIT_AUTH_KEY is missing
  const noSplitAuthKey = !process.env.REACT_APP_SPLIT_AUTH_KEY || process.env.REACT_APP_SPLIT_AUTH_KEY === 'undefined'

  return (
    <Provider store={store}>
      {noSplitAuthKey ? (
        <Grid sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
          There was an error loading the app. Please reach out to your GreenSpark representative.
        </Grid>
      ) : (
        <SplitFactoryProvider
          config={{
            core: {
              authorizationKey: process.env.REACT_APP_SPLIT_AUTH_KEY as string,
              key: 'f270caa0-9741-11ed-97ea-c228b1d93659', // using Gordon's key for now
            },
            startup: {
              retriesOnFailureBeforeReady: 3,
              readyTimeout: 60,
            },
            debug: (process.env.REACT_APP_SPLITIO_LOG_LEVEL as any) || undefined,
          }}
        >
          <Router>
            <ThemeProvider theme={theme}>
              <IntercomProvider appId={INTERCOM_APP_ID} autoBoot={isAmplifyDevEnv || isProduction}>
                <CssBaseline />
                <ErrorBoundary>
                  <AppOrLoader />
                </ErrorBoundary>
              </IntercomProvider>
            </ThemeProvider>
          </Router>
        </SplitFactoryProvider>
      )}
    </Provider>
  )
}

export default App
